import { i18n } from '@tasama/i18n';
import { createBaseStore } from '@tasama/utils';
import {
  getValidateChatInputUploadForm,
  type ChatInputUploadFormData,
  chatInputUploadDefaultValues,
} from '../schemas/chat-input-upload.schema';

const validateChatInputUploadForm = () =>
  getValidateChatInputUploadForm((key) => i18n.t(key, { ns: 'portal' }));

/**
 * Chat Input Upload store — file upload manager for chat responses
 *
 * This store manages file upload state for the ChatInput component.
 * It handles upload progress, errors, and the file list.
 * Files are optional for chat responses (minFiles: 0).
 */

export interface ChatInputUploadedFile {
  id: string;
  file?: File;
  fileName: string;
  fileSize: string;
  bytes?: number | undefined;
  status: 'uploading' | 'complete' | 'error' | 'pending';
  progress: number;
  uploadUrl?: string;
  errorMessage?: string;
}

interface ChatInputUploadState {
  // Form data
  formData: ChatInputUploadFormData;
  errors: Record<string, string>;

  // Upload state
  uploadedFiles: ChatInputUploadedFile[];
  uploadError: string | null;
  isUploading: boolean;
  uploadProgress: number;

  // Loading and error states
  loading: boolean;
  error: string | null;

  // Actions
  addFiles: (_files: ChatInputUploadedFile[]) => void;
  addUploadedFile: (_file: ChatInputUploadedFile) => void;
  removeUploadedFile: (_fileId: string) => void;
  removeFile: (_fileId: string) => void;
  updateFile: (
    _fileId: string,
    _updates: Partial<ChatInputUploadedFile>
  ) => void;
  updateFileStatus: (
    _fileId: string,
    _status: ChatInputUploadedFile['status'],
    _progress?: number,
    _errorMessage?: string
  ) => void;
  setUploadError: (_error: string | null) => void;
  clearUploadError: () => void;
  setIsUploading: (_uploading: boolean) => void;
  /** Alias for setIsUploading — satisfies the useFileUploadSubmit contract. */
  setLoading: (_loading: boolean) => void;
  setUploadProgress: (_progress: number) => void;
  validateForm: () => boolean;
  /** Checks that files are valid (optional for chat, so always returns success if empty). */
  validateFiles: () => { success: boolean; error?: string };
  /** Builds FormData from uploaded files. */
  getFormData: () => FormData | null;
  resetUploadState: () => void;
}

const initialState: any = {
  formData: chatInputUploadDefaultValues,
  errors: {},
  uploadedFiles: [],
  uploadError: null,
  isUploading: false,
  uploadProgress: 0,
};

export const useChatInputUploadStore = createBaseStore<ChatInputUploadState>(
  initialState,
  (set, get) => ({
    // Actions
    addFiles: (files: ChatInputUploadedFile[]) => {
      set((state: ChatInputUploadState) => {
        const newFiles = [...state.uploadedFiles, ...files];
        return {
          uploadedFiles: newFiles,
          formData: {
            ...state.formData,
            uploadedFiles: newFiles.map((f: ChatInputUploadedFile) => ({
              id: f.id,
              fileName: f.fileName,
              fileSize: f.fileSize,
              bytes: f.bytes,
              progress: f.progress,
              status: f.status as any,
            })),
          },
        };
      });
    },

    addUploadedFile: (file: ChatInputUploadedFile) => {
      set((state: ChatInputUploadState) => {
        const newFiles = [...state.uploadedFiles, file];
        return {
          uploadedFiles: newFiles,
          formData: {
            ...state.formData,
            uploadedFiles: newFiles.map((f: ChatInputUploadedFile) => ({
              id: f.id,
              fileName: f.fileName,
              fileSize: f.fileSize,
              bytes: f.bytes,
              progress: f.progress,
              status: f.status as any,
            })),
          },
        };
      });
    },

    removeUploadedFile: (fileId: string) => {
      set((state: ChatInputUploadState) => {
        const newFiles = state.uploadedFiles.filter(
          (file: ChatInputUploadedFile) => file.id !== fileId
        );
        return {
          uploadedFiles: newFiles,
          formData: {
            ...state.formData,
            uploadedFiles: newFiles.map((f: ChatInputUploadedFile) => ({
              id: f.id,
              fileName: f.fileName,
              fileSize: f.fileSize,
              bytes: f.bytes,
              progress: f.progress,
              status: f.status as any,
            })),
          },
        };
      });
    },

    removeFile: (fileId: string) => {
      get().removeUploadedFile(fileId);
    },

    updateFile: (fileId: string, updates: Partial<ChatInputUploadedFile>) => {
      set((state: ChatInputUploadState) => {
        const newFiles = state.uploadedFiles.map(
          (file: ChatInputUploadedFile) =>
            file.id === fileId ? { ...file, ...updates } : file
        );
        return {
          uploadedFiles: newFiles,
          formData: {
            ...state.formData,
            uploadedFiles: newFiles.map((f: ChatInputUploadedFile) => ({
              id: f.id,
              fileName: f.fileName,
              fileSize: f.fileSize,
              bytes: f.bytes,
              progress: f.progress,
              status: f.status as any,
            })),
          },
        };
      });
    },

    updateFileStatus: (
      fileId: string,
      status: ChatInputUploadedFile['status'],
      progress = 0,
      errorMessage?: string
    ) => {
      get().updateFile(fileId, { status, progress, errorMessage });
    },

    setUploadError: (error: string | null) => {
      set({
        uploadError: error,
        errors: error ? { uploadedFiles: error } : {},
      });
    },

    clearUploadError: () => {
      set({ uploadError: null, errors: {} });
    },

    setIsUploading: (uploading: boolean) => {
      set({ isUploading: uploading });
    },

    setLoading: (loading: boolean) => {
      set({ isUploading: loading });
    },

    setUploadProgress: (progress: number) => {
      set({ uploadProgress: progress });
    },

    validateForm: () => {
      const state = get();
      const result = validateChatInputUploadForm()(state.formData);

      if (result.success) {
        set({ errors: {} });
        return true;
      } else {
        set({ errors: result.errors || {} });
        return false;
      }
    },

    validateFiles: () => {
      const { uploadedFiles } = get();

      // Files are optional for chat responses, so empty is valid
      if (uploadedFiles.length === 0) {
        return { success: true };
      }

      // If files exist, check they're complete
      const hasIncompleteFiles = uploadedFiles.some(
        (f: ChatInputUploadedFile) =>
          f.status === 'uploading' || f.status === 'pending'
      );

      if (hasIncompleteFiles) {
        // Use the error message from schema
        const validationResult = validateChatInputUploadForm()({
          uploadedFiles: uploadedFiles.map((f: ChatInputUploadedFile) => ({
            id: f.id,
            fileName: f.fileName,
            fileSize: f.fileSize,
            bytes: f.bytes,
            progress: f.progress,
            status: f.status as any,
          })),
        });
        return {
          success: false,
          error:
            validationResult.errors?.uploadedFiles ||
            'Please wait until all files finish uploading',
        };
      }

      const hasErrorFiles = uploadedFiles.some(
        (f: ChatInputUploadedFile) => f.status === 'error'
      );
      if (hasErrorFiles) {
        // Use the error message from schema validation
        const validationResult = validateChatInputUploadForm()({
          uploadedFiles: uploadedFiles.map((f: ChatInputUploadedFile) => ({
            id: f.id,
            fileName: f.fileName,
            fileSize: f.fileSize,
            bytes: f.bytes,
            progress: f.progress,
            status: f.status as any,
          })),
        });
        return {
          success: false,
          error:
            validationResult.errors?.uploadedFiles ||
            'Some files failed to upload. Please remove and try again.',
        };
      }

      return { success: true };
    },

    getFormData: () => {
      const { uploadedFiles } = get();
      if (uploadedFiles.length === 0) return null;

      const formData = new FormData();
      uploadedFiles.forEach((uploadedFile: ChatInputUploadedFile) => {
        if (uploadedFile.file) {
          formData.append('files', uploadedFile.file);
        }
      });
      return formData;
    },

    resetUploadState: () => {
      set(initialState);
    },
  })
);

// Export store selectors for easy access
export const useChatInputUploadFormData = () =>
  useChatInputUploadStore((state) => state.formData);
export const useChatInputUploadErrors = () =>
  useChatInputUploadStore((state) => state.errors);
export const useChatInputUploadedFiles = () =>
  useChatInputUploadStore((state) => state.uploadedFiles);
export const useChatInputUploadError = () =>
  useChatInputUploadStore((state) => state.uploadError);
export const useChatInputIsUploading = () =>
  useChatInputUploadStore((state) => state.isUploading);
export const useChatInputUploadProgress = () =>
  useChatInputUploadStore((state) => state.uploadProgress);
